import moment from 'moment';

/**
 * Parse a date string from the DRF API which uses UK format: DD-MM-YYYY HH:mm:ss.ffffffZ
 * moment() without a format specifier would misinterpret DD-MM as MM-DD (US default).
 *
 * SEMANTICS — aligned with the canonical src/utils/taskDateUtils.js parseApiDate:
 * the trailing ``Z`` in the DRF DATETIME_FORMAT output is a LITERAL character, NOT
 * a UTC marker (Django renders Europe/London local time then appends a literal Z —
 * see the taskDateUtils module docstring). This parser therefore STRIPS the literal
 * ``Z`` (and the microsecond fraction) and reads the wall-clock value as LOCAL time,
 * the same as taskDateUtils. (Previously the format tokens ended in ``Z``, which
 * moment treats as a real UTC offset → a 1h skew vs the rest of the app and, at the
 * midnight boundary, a wrong calendar day. All consumers here do day-granularity
 * math/display, so local wall-clock is correct.)
 *
 * Returns a MOMENT instance (consumers use moment-only methods: .fromNow(), .clone(),
 * .startOf(), .diff(), etc.) — so we keep moment rather than importing taskDateUtils'
 * dayjs-based parser, while matching its local-wall-clock interpretation.
 */
const API_DATETIME_FORMATS = [
    'DD-MM-YYYY HH:mm:ss.SSSSSS',
    'DD-MM-YYYY HH:mm:ss.SSS',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY',
    moment.ISO_8601,
];

export function parseApiDate(value) {
    if (!value) return null;
    // Drop a trailing LITERAL "Z" (not a UTC marker) so moment parses the
    // wall-clock components as local time — matching taskDateUtils.parseApiDate.
    // ISO 8601 strings with a real offset (e.g. "+01:00") are left untouched and
    // handled by moment.ISO_8601 below.
    const normalized = /^\d{2}-\d{2}-\d{4}/.test(value) ? value.replace(/Z$/, '') : value;
    const parsed = moment(normalized, API_DATETIME_FORMATS, true);
    return parsed.isValid() ? parsed : null;
}

/**
 * Maps CONTACTED_VIA values to REMINDER_METHOD values.
 * CONTACTED_VIA: SMS, EMAIL, PHONE, VISITED, SOCIAL_MEDIA, ONLINE_CHAT, WHATSAPP
 * REMINDER_METHOD: CALL, EMAIL, SMS, WHATSAPP, OTHER
 */
const CONTACTED_VIA_TO_METHOD = {
    PHONE: 'CALL',
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    WHATSAPP: 'WHATSAPP',
    VISITED: 'OTHER',
    SOCIAL_MEDIA: 'OTHER',
    ONLINE_CHAT: 'OTHER',
};

/**
 * Compute the spread window (number of days) based on batch size.
 * 1-5 leads = 1 day, 6-15 = 2 days, 16-50 = 3 days.
 */
export function computeSpreadWindow(leadCount) {
    if (leadCount <= 5) return 1;
    if (leadCount <= 15) return 2;
    return 3;
}

/**
 * Match the reminder method based on the lead's last contacted_via.
 * Falls back to CALL for count 0, or the mapped method for count 1+.
 */
export function matchMethod(contactedCount, latestContactedVia) {
    if (contactedCount === 0 || !latestContactedVia) return 'CALL';
    return CONTACTED_VIA_TO_METHOD[latestContactedVia] || 'CALL';
}

/**
 * Build the reminder title based on contacted count and treatment.
 */
export function buildTitle(contactedCount, treatmentName) {
    const treatment = treatmentName || 'General';
    if (contactedCount === 0) return `Initial contact — ${treatment}`;
    if (contactedCount >= 3) return `Follow-up 3+ — ${treatment}`;
    return `Follow-up ${contactedCount} — ${treatment}`;
}

/**
 * Build the reminder note from the latest lead note (truncated to 60 chars).
 */
export function buildNote(latestNote) {
    if (!latestNote) return '';
    return latestNote.length > 60 ? `${latestNote.substring(0, 60)}...` : latestNote;
}

/**
 * Get the reminder group based on contacted count.
 */
export function getGroup(contactedCount) {
    return contactedCount === 0 ? 'NEW_LEAD' : 'FOLLOW_UP';
}

/**
 * If the given date falls on a Sunday, push it to Monday (same time).
 */
export function skipSunday(date) {
    if (date.day() === 0) date.add(1, 'day');
    return date;
}

/**
 * Assign due dates to leads spread across a window.
 *
 * Sorts leads by contacted_count ascending (newest first).
 * Distributes evenly across the spread window days.
 * Within each day, staggers by 15 minutes starting at 9:00 AM.
 *
 * @param {Array} leads - Array of lead context objects from bulk-context API
 * @returns {Array} - Same leads with `dueDate` (moment) assigned
 */
export function assignDueDates(leads, perDay) {
    if (!leads || leads.length === 0) return [];

    // Sort by contacted_count ascending (nulls treated as 0)
    const sorted = [...leads].sort(
        (a, b) => (a.contacted_count || 0) - (b.contacted_count || 0)
    );

    const spreadDays = computeSpreadWindow(sorted.length);
    const leadsPerDay = perDay || Math.ceil(sorted.length / spreadDays);

    return sorted.map((lead, index) => {
        const dayOffset = Math.floor(index / leadsPerDay);
        const slotInDay = index % leadsPerDay;

        const dueDate = skipSunday(
            moment()
                .add(dayOffset, 'days')
                .startOf('day')
                .hour(9)
                .minute(slotInDay * 15)
                .second(0)
        );

        return {
            ...lead,
            dueDate,
        };
    });
}

/**
 * Follow-up schedule intervals from consultation date.
 * Based on practice's actual process:
 *   Count 0 → cons + 2 days
 *   Count 1 → cons + 7 days
 *   Count 2 → cons + 30 days
 *   Count 3 → cons + 60 days
 *   Count 4 → cons + 150 days
 *   Count 5+ → archive candidate
 */
const FOLLOW_UP_INTERVALS_DAYS = [2, 7, 30, 60, 150];

/**
 * Assign due dates based on the practice's follow-up schedule
 * relative to consultation attended date.
 *
 * If consultation_attended_date is null, falls back to catch-up spread.
 * If the computed date is in the past, uses today at 9:00 AM + stagger.
 *
 * @param {Array} leads - Array of lead context objects
 * @returns {Array} - Same leads with `dueDate` (moment) and `isArchiveCandidate` assigned
 */
export function assignFollowUpScheduleDates(leads, perDay) {
    if (!leads || leads.length === 0) return [];

    const today = moment().startOf('day');
    const OVERDUE_LEADS_PER_DAY = perDay || 15;

    // Step 1 — compute schedule dates and classify each lead
    const classified = leads.map((lead) => {
        const consDate = parseApiDate(lead.consultation_attended_date);
        const count = lead.consultation_contacted_count ?? 0;

        // No consultation date → needs rescheduling
        if (!consDate) {
            return { ...lead, scheduleDue: null, isArchiveCandidate: false, needsReschedule: true };
        }

        // Archive candidates: 5+ follow-ups after consultation
        if (count >= 5) {
            return { ...lead, scheduleDue: null, isArchiveCandidate: true, needsReschedule: false };
        }

        // Compute next follow-up date from consultation
        const intervalDays = FOLLOW_UP_INTERVALS_DAYS[count] || FOLLOW_UP_INTERVALS_DAYS[FOLLOW_UP_INTERVALS_DAYS.length - 1];
        const scheduleDue = consDate.clone().add(intervalDays, 'days').startOf('day');
        const isOverdue = scheduleDue.isBefore(today);

        return { ...lead, scheduleDue, isArchiveCandidate: false, needsReschedule: isOverdue };
    });

    // Step 2 — separate into groups
    const archivable = classified.filter((l) => l.isArchiveCandidate);
    const futureLeads = classified.filter((l) => !l.isArchiveCandidate && !l.needsReschedule);
    const overdueLeads = classified
        .filter((l) => l.needsReschedule)
        .sort((a, b) => {
            // Most overdue first (no consDate leads last)
            if (a.scheduleDue && b.scheduleDue) return a.scheduleDue.valueOf() - b.scheduleDue.valueOf();
            if (a.scheduleDue) return -1;
            return 1;
        });

    // Step 3 — future leads keep their schedule date, staggered by 15 min when sharing a day
    const futureByDay = {};
    const futureResult = futureLeads.map((lead) => {
        const dayKey = lead.scheduleDue.format('YYYY-MM-DD');
        futureByDay[dayKey] = (futureByDay[dayKey] || 0);
        const slot = futureByDay[dayKey];
        futureByDay[dayKey] += 1;
        return {
            ...lead,
            dueDate: skipSunday(lead.scheduleDue.clone().hour(9).minute(slot * 15)),
            isOverdue: false,
        };
    });

    // Step 4 — overdue leads: preserve relative day gaps based on scheduleDue,
    // shifted forward so the earliest overdue lands on today.
    // Leads without a scheduleDue (no consultation date) go into a catch-all day.
    const withSchedule = overdueLeads.filter((l) => l.scheduleDue);
    const noSchedule = overdueLeads.filter((l) => !l.scheduleDue);

    // Find earliest scheduleDue to compute relative offsets
    const earliest = withSchedule.length > 0
        ? withSchedule.reduce((min, l) => (l.scheduleDue.isBefore(min) ? l.scheduleDue : min), withSchedule[0].scheduleDue)
        : today;

    // Group by their relative day offset from earliest, then stagger within each day
    const overdueByDay = {};
    const overdueWithScheduleResult = withSchedule.map((lead) => {
        const relDay = lead.scheduleDue.diff(earliest, 'days');
        overdueByDay[relDay] = (overdueByDay[relDay] || 0);
        const slot = overdueByDay[relDay];
        overdueByDay[relDay] += 1;
        const dueDate = skipSunday(today.clone().add(relDay, 'days').hour(9).minute(slot * 15).second(0));
        return { ...lead, dueDate, isOverdue: false };
    });

    // No-schedule leads get spread after the last relative day
    const maxRelDay = Object.keys(overdueByDay).length > 0
        ? Math.max(...Object.keys(overdueByDay).map(Number)) + 1
        : 0;
    const noScheduleResult = noSchedule.map((lead, index) => {
        const dayOff = maxRelDay + Math.floor(index / OVERDUE_LEADS_PER_DAY);
        const slot = index % OVERDUE_LEADS_PER_DAY;
        const dueDate = skipSunday(today.clone().add(dayOff, 'days').hour(9).minute(slot * 15).second(0));
        return { ...lead, dueDate, isOverdue: false };
    });

    const overdueResult = [...overdueWithScheduleResult, ...noScheduleResult];

    // Step 5 — combine: overdue (rescheduled to today+) first, then future, then archive
    return [
        ...overdueResult,
        ...futureResult,
        ...archivable.map((l) => ({ ...l, dueDate: null })),
    ];
}

/**
 * New Enquiry follow-up intervals from day of enquiry (created_at).
 *   Count 0 → enquiry day (Day 0)
 *   Count 1 → +1 day
 *   Count 2 → +5 days
 *   Count 3 → +14 days
 *   Count 4 → +30 days
 *   Count 5+ → archive candidate
 */
const NEW_ENQUIRY_INTERVALS_DAYS = [0, 1, 5, 14, 30];
const NEW_ENQUIRY_LEADS_PER_DAY = 15;

/**
 * Assign due dates for NEW status leads based on enquiry date + contact count,
 * with last-contacted-date recency protection and capacity-based daily spread.
 *
 * 1. Compute ideal due date: MAX(enquiry + interval[count], lastContact + 1 day)
 * 2. Sort by priority: most overdue first, then due today, then future
 * 3. Spread into daily buckets of ~15 leads, staggered by 15 min from 9:00 AM
 *
 * @param {Array} leads - Array of lead context objects
 * @returns {Array} - Leads with dueDate, isArchiveCandidate, isOverdue assigned
 */
export function assignNewEnquiryDates(leads, perDay) {
    if (!leads || leads.length === 0) return [];

    const today = moment().startOf('day');

    // Step 1 — compute ideal due date per lead
    const withIdealDates = leads.map((lead) => {
        const count = lead.contacted_count || 0;
        const enquiryDate = parseApiDate(lead.created_at);
        const lastContactDate = parseApiDate(lead.latest_contacted_date)
            || parseApiDate(lead.latest_note_date);

        // Archive candidate: 5+ contacts
        if (count >= 5) {
            return { ...lead, idealDue: null, isArchiveCandidate: true, isOverdue: false };
        }

        // No enquiry date (shouldn't happen) — fallback to today
        if (!enquiryDate) {
            return { ...lead, idealDue: today.clone(), isArchiveCandidate: false, isOverdue: false };
        }

        const intervalDays = NEW_ENQUIRY_INTERVALS_DAYS[count]
            ?? NEW_ENQUIRY_INTERVALS_DAYS[NEW_ENQUIRY_INTERVALS_DAYS.length - 1];

        // Schedule-based date
        const scheduleDue = enquiryDate.clone().add(intervalDays, 'days').startOf('day');

        // Recency protection: if last contact is more recent than schedule,
        // push due date to at least lastContact + 1 day
        let idealDue = scheduleDue;
        if (lastContactDate && lastContactDate.isAfter(scheduleDue)) {
            idealDue = lastContactDate.clone().add(1, 'day').startOf('day');
        }

        const isOverdue = idealDue.isBefore(today);

        return { ...lead, idealDue, isArchiveCandidate: false, isOverdue };
    });

    // Step 2 — separate archive candidates, then sort remainder by priority
    const archivable = withIdealDates.filter((l) => l.isArchiveCandidate);
    const actionable = withIdealDates
        .filter((l) => !l.isArchiveCandidate)
        .sort((a, b) => {
            // Most overdue first (smallest idealDue first)
            if (a.idealDue && b.idealDue) return a.idealDue.valueOf() - b.idealDue.valueOf();
            if (a.idealDue) return -1;
            return 1;
        });

    // Step 3 — spread into daily buckets
    // All spread dates are today or future, so isOverdue is always false here.
    const leadsPerDay = perDay || NEW_ENQUIRY_LEADS_PER_DAY;
    const result = actionable.map((lead, index) => {
        const dayOffset = Math.floor(index / leadsPerDay);
        const slotInDay = index % leadsPerDay;

        const dueDate = skipSunday(
            today.clone()
                .add(dayOffset, 'days')
                .hour(9)
                .minute(slotInDay * 15)
                .second(0)
        );

        return { ...lead, dueDate, isOverdue: false };
    });

    // Append archive candidates at the end
    return [...result, ...archivable.map((l) => ({ ...l, dueDate: null }))];
}

export const TIMING_MODES = {
    CATCH_UP: 'catch_up',
    FOLLOW_UP_SCHEDULE: 'follow_up_schedule',
    NEW_ENQUIRY: 'new_enquiry',
};

/**
 * Best-effort status from a lead row. V1 table rows have `status`. rawLeadContext
 * (BulkLeadContextResponseSerializer) doesn't — infer from response fields instead.
 *
 * Order matters: ATTENDED is signalled by `consultation_attended_date`. A lead can
 * have both an attended past consultation AND an upcoming re-engagement appointment;
 * we treat that as ATTENDED (the consultation milestone is what drives the schedule).
 */
const inferLeadStatus = (lead) => {
    if (lead?.status) return lead.status;
    if (lead?.consultation_attended_date) return 'CONSULTATION_ATTENDED';
    if (lead?.has_upcoming_appointment) return 'CONSULTATION_BOOKED';
    return 'NEW';
};

const getContactCount = (lead) => {
    if (typeof lead?.contacted_count === 'number') return lead.contacted_count;
    const entry = (lead?.lead_contacted_counts || []).find((e) => e.status === 'NEW');
    return entry?.count ?? 0;
};

/**
 * Detect the appropriate timing mode from the set of selected leads.
 * Returns { mode, block, bookedCount? } — block:true means the caller should
 * prevent scheduling until the booked leads are removed from the selection.
 *
 * Accepts lead rows from either:
 *   - V1 table rows (have `status`, contact count in `lead_contacted_counts[]`)
 *   - rawLeadContext (no `status` — inferred from `consultation_attended_date`,
 *     `has_upcoming_appointment`; contact count in `contacted_count`)
 *
 * IMPORTANT: prefer running this against `rawLeadContext` after the bulk-context
 * fetch so it covers cross-page selections. V1 table rows reflect only the
 * currently-rendered page when DataGrid uses `keepNonExistentRowsSelected`.
 */
export function detectTimingMode(leads) {
    if (!leads?.length) return { mode: TIMING_MODES.CATCH_UP, block: false };

    const statuses = leads.map(inferLeadStatus);

    const bookedCount = statuses.filter((s) => s === 'CONSULTATION_BOOKED').length;
    if (bookedCount > 0) {
        return { mode: null, block: true, bookedCount };
    }

    if (statuses.every((s) => s === 'CONSULTATION_ATTENDED')) {
        return { mode: TIMING_MODES.FOLLOW_UP_SCHEDULE, block: false };
    }

    const allNewZeroContacts = leads.every(
        (lead, i) => statuses[i] === 'NEW' && getContactCount(lead) === 0
    );
    if (allNewZeroContacts) return { mode: TIMING_MODES.NEW_ENQUIRY, block: false };

    return { mode: TIMING_MODES.CATCH_UP, block: false };
}

/**
 * Build the full preview rows from raw lead context data.
 *
 * @param {Array} leadsContext - Array from bulk-context API response
 * @param {string} timingMode - 'catch_up', 'follow_up_schedule', or 'new_enquiry'
 * @returns {Array} - Preview rows with all computed fields
 */
export function buildPreviewRows(leadsContext, timingMode, perDay) {
    const isFollowUp = timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE;
    const isNewEnquiry = timingMode === TIMING_MODES.NEW_ENQUIRY;

    let withDates;
    if (isNewEnquiry) {
        withDates = assignNewEnquiryDates(leadsContext, perDay);
    } else if (isFollowUp) {
        withDates = assignFollowUpScheduleDates(leadsContext, perDay);
    } else {
        withDates = assignDueDates(leadsContext, perDay);
    }

    return withDates.map((lead) => {
        const count = lead.contacted_count || 0;
        // For follow-up schedule: use consultation-specific count (don't fall back to total)
        const consCount = lead.consultation_contacted_count ?? 0;
        const displayCount = isFollowUp ? consCount : count;
        return {
            leadId: lead.id,
            firstName: lead.first_name,
            lastName: lead.last_name,
            contactedCount: count,
            consultationContactedCount: consCount,
            latestNote: lead.latest_note,
            // Prefer actual contact log date, fall back to any note date, then consultation date
            // parseApiDate handles the UK DD-MM-YYYY format from DRF
            latestNoteDate: parseApiDate(lead.latest_contacted_date) || parseApiDate(lead.latest_note_date) || parseApiDate(lead.consultation_attended_date),
            latestContactedVia: lead.latest_contacted_via,
            consultationAttendedDate: parseApiDate(lead.consultation_attended_date),
            enquiryDate: parseApiDate(lead.created_at),
            idealDue: lead.idealDue || null,
            method: isNewEnquiry ? 'CALL' : matchMethod(count, lead.latest_contacted_via),
            dueDate: lead.dueDate,
            group: getGroup(displayCount),
            title: buildTitle(displayCount, lead.primary_treatment_name),
            note: buildNote(lead.latest_note),
            priority: 'NORMAL',
            assignees: lead.assignees || [],
            hasUpcomingAppointment: lead.has_upcoming_appointment,
            nearestAppointmentDatetime: parseApiDate(lead.nearest_appointment_datetime),
            pendingReminderCount: lead.pending_reminder_count || 0,
            nextReminderDatetime: parseApiDate(lead.next_reminder_datetime),
            isArchiveCandidate: lead.isArchiveCandidate || false,
            isOverdue: lead.isOverdue || false,
            checked: !lead.isArchiveCandidate && !(lead.pending_reminder_count > 0), // uncheck leads with existing reminders
        };
    });
}
